import { useEffect, useState, forwardRef } from "react";
import { Product } from "@/types/stock";
import { generateBarcodeDataURL } from "@/utils/barcodeUtils";

// Simple cache for barcode data URLs to avoid regenerating the same barcode
const barcodeCache = new Map<string, string>();
const MAX_CACHE_SIZE = 50; // Limit cache size to prevent memory issues

// Helper function to manage cache size
const addToCache = (key: string, value: string) => {
  if (barcodeCache.size >= MAX_CACHE_SIZE) {
    // Remove the oldest entry (first one)
    const firstKey = barcodeCache.keys().next().value;
    if (firstKey) {
      barcodeCache.delete(firstKey);
    }
  }
  barcodeCache.set(key, value);
};

interface BarcodeStickerProps {
  product: Product;
  barcode?: string;
  className?: string;
}

/**
 * BarcodeSticker component for generating printable product stickers
 * Contains product name, EAN13 barcode, and price in a standard sticker format
 */
const BarcodeSticker = forwardRef<HTMLDivElement, BarcodeStickerProps>(
  ({ product, barcode, className = "" }, ref) => {
    const [barcodeDataURL, setBarcodeDataURL] = useState<string>("");
    const [currentBarcode, setCurrentBarcode] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");

    useEffect(() => {
      const generateBarcode = async () => {
        try {
          setLoading(true);
          setError("");

          // Determine which barcode to use
          const barcodeToUse = barcode || product.barcode;

          if (!barcodeToUse) {
            setError("No barcode available");
            setLoading(false);
            return;
          }

          // Set the current barcode
          setCurrentBarcode(barcodeToUse);

          // Check cache first to avoid regenerating the same barcode
          const cacheKey = `${barcodeToUse}-EAN13-optimized`;
          if (barcodeCache.has(cacheKey)) {
            setBarcodeDataURL(barcodeCache.get(cacheKey)!);
            setLoading(false);
            return;
          }

          // Generate barcode image optimized for 40x20mm sticker with faster settings
          // Add timeout to prevent hanging
          const dataURL = await Promise.race([
            generateBarcodeDataURL(barcodeToUse, {
              format: "EAN13", // Try EAN13 first
              width: 1, // Thinner bars for faster generation
              height: 25, // Shorter height for faster processing
              displayValue: true,
              fontSize: 6, // Smaller font for faster rendering
              textMargin: 1, // Minimal text margin
              margin: 1, // Minimal margin for small sticker
              background: "#ffffff",
              lineColor: "#000000",
            }),
            new Promise<never>((_, reject) =>
              setTimeout(
                () => reject(new Error("Barcode generation timeout")),
                3000
              )
            ),
          ]);

          // Cache the generated barcode
          addToCache(cacheKey, dataURL);
          setBarcodeDataURL(dataURL);
        } catch (err) {
          console.error("Error generating barcode:", err);

          // If EAN13 fails, try CODE128 which is more flexible
          try {
            const barcodeToUse = barcode || product.barcode;
            if (barcodeToUse) {
              // Check cache for CODE128 version
              const fallbackCacheKey = `${barcodeToUse}-CODE128-optimized`;
              if (barcodeCache.has(fallbackCacheKey)) {
                setBarcodeDataURL(barcodeCache.get(fallbackCacheKey)!);
                return;
              }

              const dataURL = await Promise.race([
                generateBarcodeDataURL(barcodeToUse, {
                  format: "CODE128",
                  width: 1, // Thinner bars for faster generation
                  height: 25, // Shorter height for faster processing
                  displayValue: true,
                  fontSize: 6, // Smaller font for faster rendering
                  textMargin: 1, // Minimal text margin
                  margin: 1, // Minimal margin for small sticker
                  background: "#ffffff",
                  lineColor: "#000000",
                }),
                new Promise<never>((_, reject) =>
                  setTimeout(
                    () => reject(new Error("CODE128 generation timeout")),
                    3000
                  )
                ),
              ]);

              // Cache the CODE128 barcode
              addToCache(fallbackCacheKey, dataURL);
              setBarcodeDataURL(dataURL);
            }
          } catch (fallbackErr) {
            console.error("Error generating fallback barcode:", fallbackErr);
            setError("Failed to generate barcode");
          }
        } finally {
          setLoading(false);
        }
      };

      generateBarcode();
    }, [product.id, product.barcode, barcode]);

    if (loading) {
      return (
        <div
          ref={ref}
          className={`bg-white border border-gray-300 p-4 rounded-lg ${className}`}
        >
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-16 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div
          ref={ref}
          className={`bg-white border border-red-300 p-4 rounded-lg ${className}`}
        >
          <div className="text-red-600 text-center">
            <p className="text-sm">{error}</p>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={`bg-white border border-gray-300 rounded-lg print:border-0 print:rounded-none print:shadow-none ${className}`}
        style={{
          width: "40mm", // 40mm width for small sticker
          height: "22mm", // Slightly taller to prevent overlap
          fontSize: "6px", // Very small font for compact sticker
          lineHeight: "1.0",
          padding: "1.5mm", // Slightly more padding
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // Distribute space evenly
        }}
      >
        {/* Product Name - Top section */}
        <div
          className="text-center"
          style={{ flex: "0 0 auto", marginBottom: "1mm" }}
        >
          <h3
            className="font-bold text-black leading-tight"
            style={{
              fontSize: "6px", // Slightly smaller to fit better
              maxHeight: "6mm", // More space for name
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2, // Allow 2 lines for better readability
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            }}
          >
            {product.name.length > 30
              ? product.name.substring(0, 30) + "..."
              : product.name}
          </h3>
        </div>

        {/* Barcode - Middle section */}
        <div
          className="flex justify-center"
          style={{ flex: "1 1 auto", minHeight: "8mm" }}
        >
          {barcodeDataURL ? (
            <img
              src={barcodeDataURL}
              alt={`Barcode: ${currentBarcode}`}
              className="max-w-full h-auto"
              style={{
                maxHeight: "8mm", // Compact height for barcode
                width: "auto",
                maxWidth: "36mm", // Leave 2mm margin on each side
                objectFit: "contain",
              }}
            />
          ) : (
            <div
              className="bg-gray-200 rounded flex items-center justify-center text-xs"
              style={{ height: "8mm", width: "36mm" }}
            >
              <span className="text-gray-500" style={{ fontSize: "4px" }}>
                No barcode
              </span>
            </div>
          )}
        </div>

        {/* Price - Bottom section */}
        <div
          className="text-center"
          style={{ flex: "0 0 auto", marginTop: "1mm" }}
        >
          <div className="font-bold text-green-600" style={{ fontSize: "6px" }}>
            {product.price.toFixed(3)} TND
          </div>
        </div>
      </div>
    );
  }
);

BarcodeSticker.displayName = "BarcodeSticker";

export default BarcodeSticker;
